/**
 * <Gate> — visibility/disable wrapper for buttons and inline actions.
 *
 * Replaces:
 *   - PermissionGuard (legacy)
 *   - PermissionButton (legacy)
 *   - PermissionShiftGate (Phase 3 stopgap)
 *
 * Modes:
 *   - "hide" (default)  — render nothing if not allowed.
 *   - "disable"         — render the child but pass `disabled` + tooltip.
 *   - "fallback"        — render the `fallback` prop instead.
 *
 * The decision is purely from `useAccess()`. The backend independently enforces
 * each API call, so a hidden/disabled Gate is a UX nicety, not security.
 *
 * Why a `requireOnShift` flag?
 *   Some actions are info-only (e.g. viewing a list) and SHOULD be available
 *   off-shift; others are destructive (e.g. approving a withdrawal) and must
 *   require the user be on-shift OR have an active Grant. Default = inherits
 *   from the catalog metadata via `requireOnShift="auto"`.
 */

import React, { cloneElement, isValidElement } from 'react';
import { Tooltip } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { useAccess } from '../../hooks/useAccess';
import {
  PERMISSION_CATALOG,
  PermissionCode,
  PermissionMeta,
} from '../../constants/permissionCatalog';

export type GateMode = 'hide' | 'disable' | 'fallback';

export interface GateProps {
  /** Permission(s) required. Logical OR (any-of). */
  permission: PermissionCode | PermissionCode[];
  /** What to do when not allowed. Default 'hide'. */
  mode?: GateMode;
  /** Used when mode='fallback'. */
  fallback?: React.ReactNode;
  /**
   * Whether to also enforce on-shift for this gate.
   *   'auto'  → reads catalog: `shiftPolicy === 'enforce'` blocks off-shift.
   *   true    → always require on-shift.
   *   false   → never (visibility based purely on permission).
   */
  requireOnShift?: 'auto' | boolean;
  /** Tooltip text shown when mode='disable'. Defaults to a sensible Persian message. */
  disabledTooltip?: string;
  children: React.ReactNode;
}

function shouldEnforceShift(
  perm: PermissionCode | PermissionCode[],
  requireOnShift: 'auto' | boolean
): boolean {
  if (typeof requireOnShift === 'boolean') return requireOnShift;
  // 'auto' — true if ANY of the permissions has shiftPolicy='enforce'.
  const arr = Array.isArray(perm) ? perm : [perm];
  for (const p of arr) {
    const meta = (PERMISSION_CATALOG as Record<string, PermissionMeta>)[p];
    if (meta?.shiftPolicy === 'enforce') return true;
  }
  return false;
}

const Gate: React.FC<GateProps> = ({
  permission,
  mode = 'hide',
  fallback = null,
  requireOnShift = 'auto',
  disabledTooltip,
  children,
}) => {
  const { has, isOnShift, activeGrants } = useAccess();

  const hasPerm = has(permission);
  const enforceShift = shouldEnforceShift(permission, requireOnShift);
  const grantCovers = (() => {
    if (!enforceShift || isOnShift) return true;
    const arr = Array.isArray(permission) ? permission : [permission];
    // A grant covers the gate when it names the permission itself, or is a
    // category-wide '*' grant for the permission's work category. Grants that
    // exist but aren't usable right now (window not open) don't count.
    return activeGrants.some((g) => {
      if (g.liveNow === false) return false;
      if (arr.includes(g.permission as PermissionCode)) return true;
      if (g.permission === '*' && g.category) {
        return arr.some(
          (p) => (PERMISSION_CATALOG as Record<string, PermissionMeta>)[p]?.category === g.category
        );
      }
      return false;
    });
  })();

  const allowed = hasPerm && (!enforceShift || isOnShift || grantCovers);

  if (allowed) return <>{children}</>;

  if (mode === 'hide') return null;
  if (mode === 'fallback') return <>{fallback}</>;

  // mode === 'disable'
  const tipText =
    disabledTooltip ??
    (!hasPerm
      ? 'برای این عمل دسترسی ندارید'
      : 'خارج از شیفت — برای انجام این عمل به شیفت یا استثناء نیاز است');

  // If child is a single React element, inject `disabled` prop. Otherwise,
  // wrap in a span and rely on visual treatment.
  if (isValidElement(children)) {
    const childProps = (children as any).props || {};
    return (
      <Tooltip title={tipText} arrow>
        <span style={{ display: 'inline-block' }}>
          {cloneElement(children as React.ReactElement, {
            ...childProps,
            disabled: true,
            'aria-disabled': true,
            startIcon: childProps.startIcon ?? <LockIcon fontSize="small" />,
          })}
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tipText} arrow>
      <span style={{ opacity: 0.4, cursor: 'not-allowed' }}>{children}</span>
    </Tooltip>
  );
};

export default Gate;
